import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Inject,
  Optional,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UserService } from '../../../services/user/user.service';
import { AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import {
  Observable,
  BehaviorSubject,
  combineLatest,
  Subscription,
  of,
  catchError,
  tap,
  map,
  switchMap,
  distinctUntilChanged,
  finalize,
  merge,
  filter,
  shareReplay,
} from 'rxjs';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EducationalPathUserDTO } from '../../../dto/educational-path/educational-path-user.dto';
import { EducationalPathItemUserDTO } from '../../../dto/educational-path/educational-path-item-user.dto';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { ParticipationEventDTO } from '../../../dto/event/participation-event.dto';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatBadgeModule } from '@angular/material/badge';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { DunaMineStationerComponent } from '../duna-mine-stationer/duna-mine-stationer.component';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  DunaValidationBadgeComponent,
  DunaValidationStatus,
} from '../duna-validation-badge/duna-validation-badge.component';
import { DunaProfileImageComponent } from '../duna-profile-image/duna-profile-image.component';
import { DunaUserProgressLabelComponent } from '../duna-user-progress-label/duna-user-progress-label.component';
import {
  DunaProfilePathDetailDialogComponent,
  DunaProfilePathDetailDialogData,
  DunaProfilePathDetailHost,
} from '../duna-profile-path-detail-dialog/duna-profile-path-detail-dialog.component';
import type { ProgressMinuteTotals } from '../duna-user-progress-label/duna-user-progress-breakdown.util';
import {
  computeTotalsFromPaths,
  filterEducationalPathsForDisplay,
} from '../duna-user-progress-label/duna-user-progress-breakdown.util';
import { isOrgCountModuleOnceEnabled } from '../duna-org-settings/duna-org-settings.component';
import { UserStatusDTO } from '../../../dto/users/user-status.dto';
import { ProfileImageService } from '../../../services/profile-image/profile-image.service';
import { UserDTO } from '../../../dto/users/user.dto';
import { ProfileImageDTO } from '../../../dto/profile-image/profile-image.dto';
import {
  dateInRoll,
  getRollBoundsForProfileYear,
  pathUserToRollInput,
} from '../../../utils/maintenance-roll-bounds.util';
import { isParticipationCompletedStatus } from '../../../services/component-data-source/data/event/event-participate-path-module-hints.util';

// Define interface for module description
interface ModuleDescription {
  language_id: number;
  description: string;
}

/** Event that contributed to a module's enrolled/completed state on this path. */
interface ModuleEventSource {
  date: string;
  name: string;
  status: string;
}

// Define interface for module items
interface ModuleItem {
  id: string;
  /** Numeric duna_modules.module_id when known — used for calendar filter deeplink. */
  moduleId: number | null;
  name: string;
  minutes: number;
  required: boolean;
  completed: boolean;
  descriptions?: ModuleDescription[];
  expanded: boolean;
  status: 'completed' | 'in_progress' | 'not_started';
  completed_at: string | null;
  participation_dates?: string[]; // Array of dates the user participated in events
  participation_event_sources?: ModuleEventSource[];
  maintenance_minutes?: number; // Maintenance minutes earned from this module
  /** Credited via manual maintenance registration (not event participation). */
  completed_via_manual?: boolean;
  inherited_from_path_name?: string | null;
}

// Define current year status
export type CurrentYearStatus = 'completed' | 'in-progress' | 'not-started';

export interface DunaProfileUserDialogData {
  user?: { uuid?: string };
  currentYear?: number;
}

export interface ProfilePathsSectionView {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  paths: EducationalPathUserDTO[];
}

@Component({
  selector: 'app-duna-profile-user',
  standalone: true,
  imports: [
    AsyncPipe,
    CommonModule,
    MatTooltipModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    FormsModule,
    DatePipe,
    MatProgressBarModule,
    MatBadgeModule,
    DunaValidationBadgeComponent,
    DunaProfileImageComponent,
    DunaUserProgressLabelComponent,
    DynamicTableComponent,
    DunaMineStationerComponent,
  ],
  templateUrl: './duna-profile-user.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-profile-user.component.scss',
})
export class DunaProfileUserComponent implements OnInit, OnDestroy {
  profileUserUuid$!: Observable<string | null>;
  profileUser$: Observable<UserDTO | null> = of(null);
  fullName$: Observable<string> = of('');
  profileImageMeta$: Observable<ProfileImageDTO | null> = of(null);

  currentUser$ = this.userService.getCurrentUser();
  private allPaths$: Observable<EducationalPathUserDTO[]> = of([]);
  achievedPaths$: Observable<EducationalPathUserDTO[]> = of([]);
  pathsSection$!: Observable<ProfilePathsSectionView>;
  pathsSection: ProfilePathsSectionView = {
    loading: true,
    error: false,
    errorMessage: '',
    paths: [],
  };

  readonly eventsTableInput = {
    dataSource: { type: 'text', value: 'profile_user_events' },
    tableTitle: { type: 'text', value: 'Dine events' },
    tableDescription: { type: 'textarea', value: '' },
  };

  /** Full page layout includes events + stationer; dialog shows education only. */
  readonly showProfileSideColumn = !this.dialogData;
  profileProgressSummary$!: Observable<{
    paths: EducationalPathUserDTO[];
    year: number;
    totals: ProgressMinuteTotals;
  }>;

  // Track subscriptions to prevent memory leaks
  private subscriptions = new Subscription();

  availableYears$: Observable<number[]> = of([]);
  selectedYear$ = new BehaviorSubject<number>(new Date().getFullYear());

  // Track expanded modules by their ID
  expandedModules: { [key: string]: boolean } = {};

  // Default language ID for descriptions
  defaultLanguageId = 0;

  // Store cached modules
  private requiredModulesCache: { [key: string]: ModuleItem[] } = {};
  private optionalModulesCache: { [key: string]: ModuleItem[] } = {};

  // Use a distinct BehaviorSubject for loading state to avoid conflicts
  isLoading$ = new BehaviorSubject<boolean>(true); // Start with loading true
  hasError$ = new BehaviorSubject<boolean>(false);
  errorMessage$ = new BehaviorSubject<string>('');

  // Track if component is destroyed to prevent state updates after ngOnDestroy
  private isDestroyed = false;

  /** Hide path 1 in the profile when the user also has path 2 (frontend-only rule). */
  /** UUID for the profile currently being displayed; validated against API responses. */
  private activeProfileUserUuid: string | null = null;
  private profileUuidSubject!: BehaviorSubject<string | null>;
  private profileImageRefresh$ = new BehaviorSubject<void>(undefined);

  /**
   * Organisation setting: modules only count once toward maintenance (same as org settings UI).
   * When true, orange "tilmeldt" bar dedupes by module and ignores sign-ups for already-completed modules.
   */
  countModuleOnce = false;

  constructor(
    private userService: UserService,
    private profileImageService: ProfileImageService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private componentDataSourceService: ComponentDataSourceService,
    private dialog: MatDialog,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private dialogData?: DunaProfileUserDialogData
  ) {

    this.profileUuidSubject = new BehaviorSubject<string | null>(
      this.dialogData?.user?.uuid ?? this.userService.getCurrentUserUuid()
    );
    this.profileUserUuid$ = this.profileUuidSubject
      .asObservable()
      .pipe(distinctUntilChanged());

    this.profileUser$ = this.profileUserUuid$.pipe(
      distinctUntilChanged(),
      switchMap((uuid) => {
        if (!uuid) {
          return of(null);
        }
        return this.userService
          .getUserByUuid(uuid)
          .pipe(catchError(() => of(null)));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.fullName$ = this.profileUser$.pipe(
      map((user) => {
        if (!user) {
          return '';
        }
        return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
      })
    );

    this.profileImageMeta$ = combineLatest([
      this.profileUserUuid$,
      this.profileImageRefresh$,
    ]).pipe(
      switchMap(([uuid]) => {
        if (!uuid) {
          return of(null);
        }
        return this.profileImageService
          .getUserProfileImage(uuid)
          .pipe(catchError(() => of(null)));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    if (!this.dialogData?.user?.uuid) {
      this.subscriptions.add(
        merge(
          of(null),
          this.router.events.pipe(
            filter(
              (event): event is NavigationEnd => event instanceof NavigationEnd
            )
          )
        )
          .pipe(
            switchMap(() => {
              const urlUuid = this.userService.getCurrentUserUuid();
              if (urlUuid) {
                return of(urlUuid);
              }
              // /profile uden UUID i URL — brug indlogget bruger (som catering-præferencer).
              return this.userService.getCurrentUserUuidFromToken();
            }),
            distinctUntilChanged()
          )
          .subscribe((uuid) => {
            if (uuid && uuid !== this.profileUuidSubject.getValue()) {
              this.profileUuidSubject.next(uuid);
            }
          })
      );
    }

    // Subscribe to loading state changes and trigger change detection
    this.subscriptions.add(
      this.isLoading$.subscribe((isLoading) => {
        // Force change detection
        this.cdr.markForCheck();
      })
    );
  }

  /**
   * Formats the renewal interval text based on recurrence value and unit
   */
  getRenewalIntervalText(path: EducationalPathUserDTO): string {
    if (!path.recurrence_value || !path.recurrence_unit) {
      return 'Ingen fornyelsesinterval';
    }

    const value = path.recurrence_value;
    const unit = path.recurrence_unit.toLowerCase();
    const recurrenceType = path.recurrence_type || 'calendar';

    // Handle different recurrence units
    let unitText = '';
    switch (unit) {
      case 'day':
        unitText = value === 1 ? 'dag' : 'dage';
        break;
      case 'month':
        unitText = value === 1 ? 'måned' : 'måneder';
        break;
      case 'year':
        unitText = value === 1 ? 'år' : 'år';
        break;
      case 'full_period':
        return 'Fornyelse ved udløb af uddannelsessti';
      default:
        unitText = unit;
    }

    let text = `Fornyelse hver ${value} ${unitText}`;

    // Add recurrence type indicator
    if (recurrenceType === 'per_user') {
      text += ' (per bruger)';
      if (path.reset_on_fulfillment) {
        text += ', nulstilles ved opfyldt vedligeholdelse';
      }
    }

    return text;
  }

  /** Human-readable maintenance block for the profile year (calendar or per-user). */
  getMaintenancePeriodRangeText(path: EducationalPathUserDTO): string | null {
    const bounds = this.getMaintenancePeriodBoundsForYear(
      path,
      this.getProfileYear()
    );
    if (!bounds) {
      return null;
    }
    return this.formatMaintenancePeriodRange(bounds.start, bounds.end);
  }

  /**
   * Label for DUNA status: the maintenance period before the profile year's block
   * (not merely calendar year − 1).
   */
  getDunaStatusPeriodLabel(path: EducationalPathUserDTO): string {
    const bounds = this.getMaintenancePeriodBoundsForYear(
      path,
      this.getProfileYear()
    );
    if (!bounds) {
      return String(this.getProfileYear() - 1);
    }
    const prevEnd = new Date(bounds.start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    prevEnd.setHours(23, 59, 59, 999);
    const prevStart = this.computeMaintenancePeriodStartFromEnd(path, prevEnd);
    return this.formatMaintenancePeriodRange(prevStart, prevEnd);
  }

  private formatMaintenancePeriodRange(start: Date, end: Date): string {
    const fmt = (d: Date) =>
      d.toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    return `${fmt(start)} – ${fmt(end)}`;
  }

  /** Maintenance roll for the profile's selected year (API window or client compute). */
  private getProfileRollBounds(
    path: EducationalPathUserDTO
  ): { start: Date; end: Date } | null {
    return getRollBoundsForProfileYear(
      pathUserToRollInput(path),
      this.getProfileYear(),
      true
    );
  }

  private getMaintenancePeriodBoundsForYear(
    path: EducationalPathUserDTO,
    year: number
  ): { start: Date; end: Date } | null {
    const preferApiWindow = year === this.getProfileYear();
    return getRollBoundsForProfileYear(
      pathUserToRollInput(path),
      year,
      preferApiWindow
    );
  }

  private computeMaintenancePeriodEnd(
    path: EducationalPathUserDTO,
    start: Date
  ): Date {
    const value = path.recurrence_value ?? 1;
    const unit = (path.recurrence_unit ?? 'year').toLowerCase();
    const recurrenceType = path.recurrence_type || 'calendar';

    if (unit === 'full_period') {
      return new Date(2099, 11, 31, 23, 59, 59);
    }

    if (
      unit === 'year' ||
      unit === 'years' ||
      (unit === 'day' && value === 365)
    ) {
      const years = unit === 'day' && value === 365 ? 1 : value;
      if (recurrenceType === 'calendar') {
        if (years === 1) {
          return new Date(start.getFullYear(), 11, 31, 23, 59, 59);
        }
        return new Date(start.getFullYear() + years - 1, 11, 31, 23, 59, 59);
      }
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + years);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      return end;
    }

    if (unit === 'month' || unit === 'months') {
      const end = new Date(start);
      end.setMonth(end.getMonth() + value);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      return end;
    }

    if (unit === 'day' || unit === 'days') {
      const end = new Date(start);
      end.setDate(end.getDate() + value - 1);
      end.setHours(23, 59, 59, 999);
      return end;
    }

    return new Date(start.getFullYear(), 11, 31, 23, 59, 59);
  }

  private computeMaintenancePeriodStartFromEnd(
    path: EducationalPathUserDTO,
    end: Date
  ): Date {
    const value = path.recurrence_value ?? 1;
    const unit = (path.recurrence_unit ?? 'year').toLowerCase();
    const recurrenceType = path.recurrence_type || 'calendar';

    if (unit === 'full_period') {
      return new Date(end);
    }

    if (
      unit === 'year' ||
      unit === 'years' ||
      (unit === 'day' && value === 365)
    ) {
      const years = unit === 'day' && value === 365 ? 1 : value;
      if (recurrenceType === 'calendar') {
        if (years === 1) {
          return new Date(end.getFullYear(), 0, 1);
        }
        return new Date(end.getFullYear() - years + 1, 0, 1);
      }
      const start = new Date(end);
      start.setFullYear(start.getFullYear() - years);
      start.setDate(start.getDate() + 1);
      start.setHours(0, 0, 0, 0);
      return start;
    }

    if (unit === 'month' || unit === 'months') {
      const start = new Date(end);
      start.setMonth(start.getMonth() - value);
      start.setDate(start.getDate() + 1);
      start.setHours(0, 0, 0, 0);
      return start;
    }

    if (unit === 'day' || unit === 'days') {
      const start = new Date(end);
      start.setDate(start.getDate() - value + 1);
      start.setHours(0, 0, 0, 0);
      return start;
    }

    return new Date(end.getFullYear(), 0, 1);
  }

  private getNextPeriodStartAfter(path: EducationalPathUserDTO, end: Date): Date {
    const next = new Date(end);
    next.setDate(next.getDate() + 1);
    next.setHours(0, 0, 0, 0);
    return next;
  }

  /**
   * Get maintenance deadline text
   * For per-user recurrence, calculates the next deadline from the maintenance_started_at date
   * For calendar-based, uses recurrence_window_end
   * Deadlines never expire - they renew at the interval
   */
  getMaintenanceDeadline(path: EducationalPathUserDTO): string | null {
    const recurrenceType = path.recurrence_type || 'calendar';

    // Debug logging

    // For per-user recurrence, calculate the next deadline from maintenance_started_at
    if (recurrenceType === 'per_user') {
      const periodAnchor = this.getMaintenancePeriodAnchor(path);
      if (
        !periodAnchor ||
        !path.recurrence_value ||
        !path.recurrence_unit
      ) {
        return null;
      }

      const startDate = new Date(periodAnchor);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const value = path.recurrence_value;
      const unit = path.recurrence_unit.toLowerCase();

      if (unit === 'full_period') {
        // No deadline for full period
        return null;
      }

      // Find the next deadline (start of next period) by adding periods from the start date
      // The deadline is the START of the next period, not the end of current period
      let nextDeadline = new Date(startDate);

      // Add the first period to get the START of the next period (the deadline)
      if (unit === 'day' || unit === 'days') {
        nextDeadline.setDate(nextDeadline.getDate() + value);
      } else if (unit === 'week' || unit === 'weeks') {
        nextDeadline.setDate(nextDeadline.getDate() + value * 7);
      } else if (unit === 'month' || unit === 'months') {
        nextDeadline.setMonth(nextDeadline.getMonth() + value);
      } else if (unit === 'year' || unit === 'years') {
        nextDeadline.setFullYear(nextDeadline.getFullYear() + value);
      } else {
        return null;
      }

      // Keep adding periods until we find one that's in the future
      while (nextDeadline <= today) {
        // Calculate start of next period (which is the deadline for current period)
        if (unit === 'day' || unit === 'days') {
          nextDeadline.setDate(nextDeadline.getDate() + value);
        } else if (unit === 'week' || unit === 'weeks') {
          nextDeadline.setDate(nextDeadline.getDate() + value * 7);
        } else if (unit === 'month' || unit === 'months') {
          nextDeadline.setMonth(nextDeadline.getMonth() + value);
        } else if (unit === 'year' || unit === 'years') {
          nextDeadline.setFullYear(nextDeadline.getFullYear() + value);
        }
      }

      // Calculate days until deadline
      const diffTime = nextDeadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Udløber i dag';
      } else if (diffDays <= 30) {
        return `Udløber om ${diffDays} dage (${nextDeadline.toLocaleDateString(
          'da-DK'
        )})`;
      } else {
        return nextDeadline.toLocaleDateString('da-DK');
      }
    }

    // Calendar-based: deadline is start of next maintenance block (day after current block ends)
    const calendarBounds = this.getMaintenancePeriodBoundsForYear(
      path,
      new Date().getFullYear()
    );
    if (calendarBounds) {
      let blockEnd = calendarBounds.end;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      while (blockEnd < today) {
        const nextStart = this.getNextPeriodStartAfter(path, blockEnd);
        blockEnd = this.computeMaintenancePeriodEnd(path, nextStart);
      }
      const nextDeadline = this.getNextPeriodStartAfter(path, blockEnd);
      const diffTime = nextDeadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Udløber i dag';
      }
      if (diffDays <= 30) {
        return `Udløber om ${diffDays} dage (${nextDeadline.toLocaleDateString(
          'da-DK'
        )})`;
      }
      return nextDeadline.toLocaleDateString('da-DK');
    }

    return null;
  }

  /**
   * Check if maintenance deadline is approaching (within 30 days)
   */
  isMaintenanceDeadlineApproaching(path: EducationalPathUserDTO): boolean {
    if (
      !path.maintenance_started_at ||
      !path.recurrence_value ||
      !path.recurrence_unit
    ) {
      // Fallback to window_end if available
      if (path.recurrence_window_end) {
        const deadline = new Date(path.recurrence_window_end);
        const today = new Date();
        const diffTime = deadline.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 30;
      }
      return false;
    }

    const recurrenceType = path.recurrence_type || 'calendar';

    // For per-user recurrence, calculate the next deadline
    if (recurrenceType === 'per_user') {
      if (
        !path.maintenance_started_at ||
        !path.recurrence_value ||
        !path.recurrence_unit
      ) {
        return false;
      }

      const startDate = new Date(path.maintenance_started_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const value = path.recurrence_value;
      const unit = path.recurrence_unit.toLowerCase();

      if (unit === 'full_period') {
        return false;
      }

      // Calculate next deadline (start of next period)
      let nextDeadline = new Date(startDate);

      if (unit === 'day' || unit === 'days') {
        nextDeadline.setDate(nextDeadline.getDate() + value);
      } else if (unit === 'week' || unit === 'weeks') {
        nextDeadline.setDate(nextDeadline.getDate() + value * 7);
      } else if (unit === 'month' || unit === 'months') {
        nextDeadline.setMonth(nextDeadline.getMonth() + value);
      } else if (unit === 'year' || unit === 'years') {
        nextDeadline.setFullYear(nextDeadline.getFullYear() + value);
      } else {
        return false;
      }

      // Keep adding periods until we find one that's in the future
      while (nextDeadline <= today) {
        if (unit === 'day' || unit === 'days') {
          nextDeadline.setDate(nextDeadline.getDate() + value);
        } else if (unit === 'week' || unit === 'weeks') {
          nextDeadline.setDate(nextDeadline.getDate() + value * 7);
        } else if (unit === 'month' || unit === 'months') {
          nextDeadline.setMonth(nextDeadline.getMonth() + value);
        } else if (unit === 'year' || unit === 'years') {
          nextDeadline.setFullYear(nextDeadline.getFullYear() + value);
        }
      }

      const diffTime = nextDeadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays >= 0 && diffDays <= 30;
    }

    const calendarBounds = this.getMaintenancePeriodBoundsForYear(
      path,
      new Date().getFullYear()
    );
    if (calendarBounds) {
      let blockEnd = calendarBounds.end;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      while (blockEnd < today) {
        const nextStart = this.getNextPeriodStartAfter(path, blockEnd);
        blockEnd = this.computeMaintenancePeriodEnd(path, nextStart);
      }
      const nextDeadline = this.getNextPeriodStartAfter(path, blockEnd);
      const diffTime = nextDeadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 30;
    }

    return false;
  }

  /**
   * Check if the educational path version is from a different organization
   */
  hasCrossOrgVersion(path: EducationalPathUserDTO): boolean {
    return path.is_cross_organization_version === true;
  }

  // Determines if a provided label is effectively a placeholder and should not be shown
  private isPlaceholderLabel(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    const v = String(value).trim().toLowerCase();
    if (v.length === 0) return true;

    // Common placeholder variants coming from backend or defaults
    const placeholders = new Set([
      'unknown path',
      'unknown',
      'unavngivet uddannelsessti',
      'unavngivet',
      'ukendt uddannelsessti',
      'ukendt',
      'unavngivet sti',
    ]);
    return placeholders.has(v);
  }

  // Picks the most appropriate display name, preferring a non-placeholder `name`,
  // then a non-placeholder `full_name`, and finally a hard fallback.
  private getDisplayPathName(name?: string, fullName?: string): string {
    const candidateName = this.isPlaceholderLabel(name)
      ? ''
      : (name || '').trim();
    const candidateFullName = this.isPlaceholderLabel(fullName)
      ? ''
      : (fullName || '').trim();
    return (
      candidateName || candidateFullName || fullName || name || 'Unknown Path'
    );
  }

  ngOnInit() {
    // Initialize loading state
    this.isLoading$.next(true);

    if (this.dialogData?.currentYear != null) {
      this.selectedYear$.next(this.dialogData.currentYear);
    }

    // Setup the paths observable and start loading immediately (not via template async pipe)
    this.setupPathsObservable();
    this.subscriptions.add(this.allPaths$.subscribe());
    this.subscriptions.add(this.achievedPaths$.subscribe());
    this.subscriptions.add(
      this.pathsSection$.subscribe((section) => {
        if (this.isDestroyed) {
          return;
        }
        this.pathsSection = section;
        this.cdr.markForCheck();
      })
    );

    // Check for year parameter in the URL and trigger initial load
    const yearSub = this.route.queryParams.subscribe((params) => {
      if (this.isDestroyed) return;

      const yearParam = params['year']
        ? parseInt(params['year'], 10)
        : new Date().getFullYear();
      if (!isNaN(yearParam) && yearParam !== this.selectedYear$.getValue()) {
        this.selectedYear$.next(yearParam);
      }
    });

    this.subscriptions.add(yearSub);

    this.subscriptions.add(
      this.currentUser$
        .pipe(
          switchMap((user) => {
            const orgId = user?.organizations_id;
            if (orgId == null || orgId <= 0) {
              return of(false);
            }
            const obs = this.componentDataSourceService.executeDataSource(
              'org_settings',
              {
                organizationId: orgId,
              }
            );
            if (!obs) {
              return of(false);
            }
            return obs.pipe(
              map((res: { data?: Record<string, unknown> }) =>
                isOrgCountModuleOnceEnabled(res?.data ?? null)
              ),
              catchError(() => of(false))
            );
          })
        )
        .subscribe((on) => {
          if (this.isDestroyed) return;
          this.countModuleOnce = !!on;
          this.cdr.markForCheck();
        })
    );
  }

  private setupPathsObservable() {
    this.allPaths$ = combineLatest([
      this.profileUserUuid$,
      this.selectedYear$,
    ]).pipe(
      distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
      tap(([userUuid]) => {
        if (this.isDestroyed) return;
        if (userUuid !== this.activeProfileUserUuid) {
          this.resetProfileState(userUuid);
        }
        if (!userUuid) {
          this.isLoading$.next(false);
          return;
        }
        this.isLoading$.next(true);
        this.hasError$.next(false);
        this.errorMessage$.next('');
      }),
      switchMap(([userUuid, selectedYear]) => {
        if (!userUuid) {
          return of([] as EducationalPathUserDTO[]);
        }

        return this.userService
          .getAchievedPathsForUuid(userUuid, selectedYear)
          .pipe(
            map((paths) => {
              if (
                paths.length > 0 &&
                !this.pathsBelongToUser(paths, userUuid)
              ) {
                console.warn(
                  '[DunaProfileUser] Stale path data ignored for UUID:',
                  userUuid
                );
                return [] as EducationalPathUserDTO[];
              }
              return paths;
            }),
            tap(() => {
              if (!this.isDestroyed) {
                this.activeProfileUserUuid = userUuid;
              }
            }),
            catchError((error) => {
              if (!this.isDestroyed) {
                this.hasError$.next(true);
                this.errorMessage$.next(
                  'Der opstod en fejl ved indlæsning af uddannelsesstier. Prøv igen senere.'
                );
              }
              return of([] as EducationalPathUserDTO[]);
            }),
            finalize(() => {
              if (!this.isDestroyed) {
                this.isLoading$.next(false);
                this.cdr.markForCheck();
              }
            })
          );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // Extract years from data_year arrays in paths
    this.availableYears$ = this.allPaths$.pipe(
      map((paths) => {
        // Use the normalization method to ensure consistent data structure
        const normalizedPaths = this.normalizePaths(paths);

        const years = new Set<number>();
        const currentYear = new Date().getFullYear();

        // Extract years from data_year arrays in each path
        normalizedPaths.forEach((path) => {
          if (path.data_year && Array.isArray(path.data_year)) {
            path.data_year.forEach((year) => {
              if (typeof year === 'number' && !isNaN(year)) {
                years.add(year);
              }
            });
          }
        });

        // Always ensure current year is included
        years.add(currentYear);

        const availableYears = Array.from(years).sort((a, b) => b - a); // Sort descending

        return availableYears;
      })
    );

    // Show ALL paths that the user is assigned to, but filter by selected year for display purposes
    this.achievedPaths$ = combineLatest([
      this.allPaths$,
      this.selectedYear$,
    ]).pipe(
      map(([paths, selectedYear]) => {
        // Safety check - ensure paths is an array
        if (!paths || !Array.isArray(paths)) {
          return [];
        }

        // First normalize the paths data structure
        const normalizedPaths = this.normalizePaths(paths);

        // Show ALL paths that the user is assigned to, but filter by selected year for display purposes
        const currentYear = new Date().getFullYear();

        let yearFilteredPaths: EducationalPathUserDTO[];

        // For current year, show all assigned paths
        if (selectedYear === currentYear) {
          yearFilteredPaths = normalizedPaths;
        } else {
          // For historical years, show paths that have data for that year OR were active during that year
          yearFilteredPaths = normalizedPaths.filter((path) => {
            // CRITICAL FIX: Always include paths that are assigned to the user
            // This addresses the issue where some paths are excluded from the user profile

            // First priority: If path has explicit data for the selected year
            if (path.data_year && Array.isArray(path.data_year)) {
              const hasDataForYear = path.data_year.includes(selectedYear);
              if (hasDataForYear) {
                return true;
              }
            }

            // Second priority: If path was assigned and is still active (not completed or expired)
            if (path.assigned_at) {
              const assignedYear = new Date(path.assigned_at).getFullYear();
              // Show path if it was assigned on or before the selected year
              if (assignedYear <= selectedYear) {
                return true;
              }
            }

            const periodBounds = this.getMaintenancePeriodBoundsForYear(
              path,
              selectedYear
            );
            if (periodBounds) {
              const selectedYearStart = new Date(selectedYear, 0, 1).getTime();
              const selectedYearEnd = new Date(
                selectedYear,
                11,
                31,
                23,
                59,
                59
              ).getTime();
              const pathStart = periodBounds.start.getTime();
              const pathEnd = periodBounds.end.getTime();
              if (pathStart <= selectedYearEnd && pathEnd >= selectedYearStart) {
                return true;
              }
            }

            // Fourth priority: If path has any participation events or requirements for this year
            if (
              path.participation_events &&
              Array.isArray(path.participation_events)
            ) {
              const rollBounds = this.getMaintenancePeriodBoundsForYear(
                path,
                selectedYear
              );
              const hasEventsInYear = path.participation_events.some(
                (event) => dateInRoll(event.date, rollBounds)
              );

              if (hasEventsInYear) {
                return true;
              }
            }

            // Fifth priority: If it's only one year back and path might still be relevant
            if (selectedYear === currentYear - 1) {
              return true;
            }

            return false;
          });
        }

        const visiblePaths = this.applyHardcodedPathVisibilityRules(
          yearFilteredPaths,
          normalizedPaths
        );
        return visiblePaths;
      }),
      tap((filteredPaths) => {
        // Output paths for debugging
        if (filteredPaths.length === 0) {
        } else {
          filteredPaths.forEach((path, index) => {
          });
        }
      }),
      // Catch and handle errors at this level too
      catchError((error) => {
        console.error(
          '[DunaProfileUser] Error in achievedPaths$ stream:',
          error
        );
        this.hasError$.next(true);
        this.errorMessage$.next(
          'Der opstod en fejl ved filtrering af uddannelsesstier.'
        );
        return of([]);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.pathsSection$ = combineLatest([
      this.isLoading$,
      this.hasError$,
      this.errorMessage$,
      this.achievedPaths$,
    ]).pipe(
      map(([loading, error, errorMessage, paths]) => ({
        loading,
        error,
        errorMessage,
        paths: paths ?? [],
      })),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.profileProgressSummary$ = combineLatest([
      this.achievedPaths$,
      this.selectedYear$,
    ]).pipe(
      map(([paths, year]) => {
        const displayPaths = filterEducationalPathsForDisplay(paths);
        const enriched = displayPaths.map((p) =>
          this.enrichPathForProgressLabel(p)
        );
        const totals = computeTotalsFromPaths(enriched, {
          countModuleOnce: this.countModuleOnce,
        });
        return {
          paths: enriched,
          year,
          totals: totals satisfies ProgressMinuteTotals,
        };
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.fullName$.subscribe((fullName) => {
    });
  }

  /**
   * Frontend-only visibility rule: path 1 is hidden when path 2 is also assigned.
   */
  private applyHardcodedPathVisibilityRules(
    paths: EducationalPathUserDTO[],
    allAssignedPaths: EducationalPathUserDTO[]
  ): EducationalPathUserDTO[] {
    return filterEducationalPathsForDisplay(paths, allAssignedPaths);
  }

  private resetProfileState(userUuid: string | null): void {
    this.activeProfileUserUuid = userUuid;
    this.requiredModulesCache = {};
    this.optionalModulesCache = {};
    this.expandedModules = {};
  }

  private pathsBelongToUser(
    paths: EducationalPathUserDTO[],
    userUuid: string
  ): boolean {
    return paths.every((path) => {
      const pathUserUuid = path.user?.uuid;
      return !pathUserUuid || pathUserUuid === userUuid;
    });
  }

  private getModuleCacheUserKey(): string {
    return this.activeProfileUserUuid ?? 'unknown-user';
  }

  getProfileInitials(user: UserDTO | null): string {
    if (!user) {
      return '?';
    }
    const first = user.first_name?.charAt(0) ?? '';
    const last = user.last_name?.charAt(0) ?? '';
    const initials = `${first}${last}`.trim();
    return initials || '?';
  }

  onProfileImageChanged(): void {
    this.profileImageRefresh$.next();
  }

  trackPathRow(index: number, path: EducationalPathUserDTO): string | number {
    const id = path.path_id ?? path.guid ?? index;
    return `${id}-${index}`;
  }

  ngOnDestroy() {
    // Mark component as destroyed
    this.isDestroyed = true;

    // Clean up subscriptions
    this.subscriptions.unsubscribe();

    // Clear behavior subjects
    this.isLoading$.complete();
    this.hasError$.complete();
    this.errorMessage$.complete();
    this.selectedYear$.complete();
    this.profileUuidSubject.complete();
    this.profileImageRefresh$.complete();
  }

  /**
   * Handles retry functionality when loading fails
   */
  retryLoading() {
    // Trigger a refresh by re-emitting the current selected year
    const currentYear = this.selectedYear$.getValue();
    this.selectedYear$.next(currentYear);
  }

  /** Opens full path details in a modal instead of inline expand. */
  openPathDetailDialog(
    path: EducationalPathUserDTO,
    pathIndex: number,
    event: Event
  ): void {
    event.stopPropagation();
    const data: DunaProfilePathDetailDialogData = {
      path,
      pathIndex,
      host: this as unknown as DunaProfilePathDetailHost,
    };
    this.dialog.open(DunaProfilePathDetailDialogComponent, {
      data,
      width: '820px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'duna-path-detail-dialog-panel',
    });
  }

  /**
   * Toggles the expanded state of a module by ID
   */
  toggleModuleById(moduleId: string): void {
    this.expandedModules[moduleId] = !this.expandedModules[moduleId];

    // Update cached modules to reflect the change
    this.updateModuleExpandedState(this.requiredModulesCache, moduleId);
    this.updateModuleExpandedState(this.optionalModulesCache, moduleId);
  }

  /**
   * Update the expanded state of modules in a cache
   */
  private updateModuleExpandedState(
    cache: { [key: string]: ModuleItem[] },
    moduleId: string
  ): void {
    Object.values(cache).forEach((modules) => {
      const module = modules.find((m) => m.id === moduleId);
      if (module) {
        module.expanded = this.expandedModules[moduleId] || false;

        // Debug log for expanded state
      }
    });
  }

  /**
   * Returns the best available description for a module.
   * Priority: Danish ('da' or '0') → any other non-empty description.
   */
  getModuleDescription(module: ModuleItem): string {
    if (!module.descriptions || module.descriptions.length === 0) {
      return '';
    }

    // Prefer Danish or the legacy language_code '0'
    const preferred = (module.descriptions as any[]).find(
      (d: any) =>
        d.language_code === 'da' ||
        d.language_code === '0' ||
        d.language_id === 0
    );
    if (preferred?.description) return preferred.description;

    // Fall back to any non-empty description
    const fallback = module.descriptions.find((d) => d.description?.trim());
    return fallback?.description ?? '';
  }

  /**
   * Updates the selected year and modifies the URL if not the current year
   */
  onYearChange(year: number): void {

    const currentYear = new Date().getFullYear();

    // Update URL if not current year
    if (year !== currentYear) {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: { year: year },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          // Only update the selected year after URL update is complete
          this.selectedYear$.next(year);
        });
    } else {
      // Remove year parameter from URL for current year
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: { year: null },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          // Only update the selected year after URL update is complete
          this.selectedYear$.next(year);
        });
    }
  }

  /**
   * Converts status code to human-readable text
   */
  getStatusText(status: string): string {
    // If no status provided, get current status based on maintenance
    if (!status) {
      return 'Ukendt';
    }

    switch (status) {
      case 'completed':
        return 'Gennemført';
      case 'in_progress':
        return 'Igangværende';
      case 'pending':
        return 'Afventer';
      case 'not_started':
        return 'Ikke påbegyndt';
      default:
        return 'Ukendt';
    }
  }

  /**
   * Calculates percentage of maintenance minutes completed (green bar segment)
   */
  getMaintenanceProgressPercentage(path: EducationalPathUserDTO): number {
    if (
      !path.maintenance_minutes_required ||
      path.maintenance_minutes_required === 0
    ) {
      return 0;
    }

    const capped = this.getMaintenanceCompletedMinutesDisplayed(path);
    const percentage =
      (capped / path.maintenance_minutes_required) * 100;
    return Math.min(percentage, 100);
  }

  /** Total tilmeldt % (deltaget + åbne tilmeldinger) — same basis as progress label. */
  getMaintenanceRegisteredPercentage(path: EducationalPathUserDTO): number {
    const { registered, required } =
      this.resolveMaintenanceRegisteredMinutes(path);
    if (required <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((100 * registered) / required));
  }

  /**
   * Orange bar segment: tilmeldt minus deltaget (matches `duna-user-progress-label` bar).
   */
  getMaintenanceRegisteredBarExtraPercentage(
    path: EducationalPathUserDTO
  ): number {
    return Math.max(
      0,
      this.getMaintenanceRegisteredPercentage(path) -
        this.getMaintenanceProgressPercentage(path)
    );
  }

  private resolveMaintenanceRegisteredMinutes(path: EducationalPathUserDTO): {
    completed: number;
    registered: number;
    required: number;
  } {
    const required = path.maintenance_minutes_required ?? 0;
    const completed = this.getMaintenanceCompletedMinutesDisplayed(path);
    const orange = this.getMaintenanceIntendedMinutes(path);
    let registered = Math.min(required, completed + orange);
    if (
      this.countModuleOnce &&
      this.areAllPathMaintenanceModulesCredited(path)
    ) {
      registered = completed;
    }
    return { completed, registered, required };
  }

  /**
   * Same minute basis as path bars — used by `app-duna-user-progress-label` on profile
   * (API path rows can show 0 completed while the profile roll has credited modules).
   */
  private enrichPathForProgressLabel(
    path: EducationalPathUserDTO
  ): EducationalPathUserDTO {
    const { completed, registered, required } =
      this.resolveMaintenanceRegisteredMinutes(path);
    const progressPercentage =
      required > 0
        ? Math.min(100, Math.round((100 * completed) / required))
        : (path.progress_percentage ?? 0);
    const registeredPercentage =
      required > 0
        ? Math.min(100, Math.round((100 * registered) / required))
        : this.getMaintenanceRegisteredPercentage(path);
    return {
      ...path,
      maintenance_minutes_completed: completed,
      maintenance_minutes_intended: registered,
      progress_percentage: progressPercentage,
      registered_percentage: registeredPercentage,
    };
  }

  /** Completed minutes shown in bars and x/y labels (capped at path requirement). */
  getMaintenanceCompletedMinutesDisplayed(
    path: EducationalPathUserDTO
  ): number {
    const req = path.maintenance_minutes_required ?? 0;
    if (req <= 0) {
      return 0;
    }
    if (this.getMaintenanceManualAcquireAll(path)) {
      return req;
    }

    const fromCreditedModules =
      this.getCompletedMaintenanceMinutesFromCreditedModules(path);
    if (fromCreditedModules !== null) {
      return Math.min(
        req,
        fromCreditedModules + this.getMaintenanceManualMinutes(path)
      );
    }

    const done = path.maintenance_minutes_completed ?? 0;
    return Math.min(req, done);
  }

  /**
   * Deltaget vedligeholdelse ud fra gennemførte moduler i profilårets roll
   * (path items, deltagelse og manuelle moduler) — undgår fx SUM på tværs af år fra API.
   */
  private getCompletedMaintenanceMinutesFromCreditedModules(
    path: EducationalPathUserDTO
  ): number | null {
    const hasMaintenanceItems =
      Array.isArray(path.maintenance) && path.maintenance.length > 0;
    const hasParticipation =
      Array.isArray(path.participation_events) &&
      path.participation_events.length > 0;
    if (!hasMaintenanceItems && !hasParticipation) {
      return null;
    }
    const credited = this.getCompletedMaintenanceModuleIds(path);
    return this.sumPathMaintenanceMinutesForModuleIds(path, credited);
  }

  /**
   * All maintenance modules on the path are credited in the profile roll (deltaget / manuelt).
   * Fremtidige gentilmeldinger til samme moduler må ikke øge "tilmeldt" når count_module_once er slået til.
   */
  private areAllPathMaintenanceModulesCredited(
    path: EducationalPathUserDTO
  ): boolean {
    const mods = this.getPathMaintenanceModuleIdSet(path);
    if (mods.size === 0) {
      return false;
    }
    const credited = this.getCompletedMaintenanceModuleIds(path);
    for (const id of mods) {
      if (!credited.has(id)) {
        return false;
      }
    }
    return true;
  }

  /** Tooltip for maintenance bars; includes manual minutes when present for the selected reporting year. */
  getMaintenanceBarTooltip(path: EducationalPathUserDTO): string {
    const { completed, registered, required } =
      this.resolveMaintenanceRegisteredMinutes(path);
    const manual = this.getMaintenanceManualMinutes(path);
    const lines = [
      `Gennemført: ${(completed / 60).toFixed(1)} t`,
      `Tilmeldt: ${(registered / 60).toFixed(1)} t`,
      `Krævet: ${(required / 60).toFixed(1)} t`,
    ];
    if (manual > 0) {
      lines.push(`Heraf manuelt registreret: ${(manual / 60).toFixed(1)} t`);
    }
    if (this.getMaintenanceManualAcquireAll(path)) {
      lines.push(
        'Manuel markering: vedligeholdelseskrav opfyldt for det valgte år'
      );
    }
    return lines.join('\n');
  }

  getMaintenanceManualMinutes(path: EducationalPathUserDTO): number {
    return Math.max(0, Number(path.maintenance_minutes_manual ?? 0));
  }

  /** Normalizes API/runtime JSON (boolean | string | number) without widening DTO types. */
  private coerceMaintenanceManualAcquireAll(raw: unknown): boolean {
    return raw === true || raw === 1 || raw === 'true';
  }

  getMaintenanceManualAcquireAll(path: EducationalPathUserDTO): boolean {
    return this.coerceMaintenanceManualAcquireAll(
      path.maintenance_manual_acquire_all
    );
  }

  private getManualCompletedModuleIdSet(
    path: EducationalPathUserDTO
  ): Set<number> {
    const ids = new Set<number>();
    const list = path.manual_completed_modules;
    if (!Array.isArray(list)) {
      return ids;
    }
    for (const entry of list) {
      const id = Number(entry?.module_id);
      if (Number.isFinite(id) && id > 0) {
        ids.add(id);
      }
    }
    return ids;
  }

  /**
   * Whether a module is credited via manual registration for the profile year.
   * Required modules may also be covered by acquire_all_needed.
   */
  private isModuleCompletedViaManual(
    path: EducationalPathUserDTO,
    moduleIdNum: number | null,
    isRequired: boolean
  ): boolean {
    if (moduleIdNum == null) {
      return false;
    }
    if (this.getManualCompletedModuleIdSet(path).has(moduleIdNum)) {
      return true;
    }
    return isRequired && this.getMaintenanceManualAcquireAll(path);
  }

  private applyManualCompletionToModule(
    path: EducationalPathUserDTO,
    moduleIdNum: number | null,
    isRequired: boolean,
    module: ModuleItem
  ): ModuleItem {
    const viaManual = this.isModuleCompletedViaManual(
      path,
      moduleIdNum,
      isRequired
    );
    if (!viaManual) {
      return module;
    }
    module.completed_via_manual = true;
    if (!module.completed) {
      module.completed = true;
      module.status = 'completed';
    }
    return module;
  }

  /** Show manual completion line under timer (same slot as event date). */
  showModuleManualCompletion(module: ModuleItem): boolean {
    return !!module.completed_via_manual && !this.showModuleEventDate(module);
  }

  getModuleManualCompletionDateLabel(): string {
    return 'Gennemført';
  }

  getModuleManualCompletionDateValue(): string {
    return 'via manuel registrering';
  }

  getModuleManualCompletionTooltip(
    path: EducationalPathUserDTO,
    module: ModuleItem
  ): string {
    if (!module.completed_via_manual) {
      return '';
    }
    if (this.getMaintenanceManualAcquireAll(path)) {
      return 'Modulet er registreret som gennemført via manuel vedligeholdelse («opnået alt nødvendigt»).';
    }
    return 'Modulet er registreret som gennemført via manuelle vedligeholdelsesminutter for det valgte år.';
  }

  showMaintenanceManualExtras(path: EducationalPathUserDTO): boolean {
    return (
      this.getMaintenanceManualMinutes(path) > 0 ||
      this.getMaintenanceManualAcquireAll(path)
    );
  }

  /** År der sendes til API og matcher manuelle registreringer (`manual-minutes.year`). */
  getProfileYear(): number {
    return this.selectedYear$.getValue();
  }

  /**
   * Maintenance minutes represented by orange bar (signed up, not yet completed as path items).
   * When {@link countModuleOnce} is on: one row per module, and modules already completed on the path are excluded.
   */
  private getMaintenanceInProgressMinutesEffective(
    path: EducationalPathUserDTO
  ): number {
    const items = this.ensureCompatibleItemFormat(
      path.maintenance || [],
      false
    );
    const completedModuleIds = new Set<number>();
    for (const item of items) {
      const mid = this.getPathItemModuleId(item);
      if (mid != null && (item.completed || item.status === 'completed')) {
        completedModuleIds.add(mid);
      }
    }
    const creditedInRoll = this.getCompletedMaintenanceModuleIds(path);
    const inProg = items.filter((item) => {
      const mid = this.getPathItemModuleId(item);
      if (mid != null && creditedInRoll.has(mid)) {
        return false;
      }
      return (
        !item.completed &&
        item.status !== 'completed' &&
        (item.status === 'in_progress' ||
          item.status === 'pending' ||
          item.status === 'waitlist')
      );
    });

    const phantom =
      this.getDedupedFullOverlapParticipationMaintenanceMinutes(path);

    if (!this.countModuleOnce) {
      const raw = inProg.reduce(
        (sum, item) => sum + this.getPathItemMaintenanceMinutes(item),
        0
      );
      return Math.max(0, raw - phantom);
    }

    const byModule = new Map<number, number>();
    for (const item of inProg) {
      const mid = this.getPathItemModuleId(item);
      if (mid == null || completedModuleIds.has(mid)) {
        continue;
      }
      const mins = this.getPathItemMaintenanceMinutes(item);
      if (mins <= 0) {
        continue;
      }
      byModule.set(mid, Math.max(byModule.get(mid) ?? 0, mins));
    }
    let total = 0;
    byModule.forEach((v) => {
      total += v;
    });
    return Math.max(0, total - phantom);
  }

  /** Intended (orange) maintenance minutes after org rules; for tooltips and legend. */
  getMaintenanceIntendedMinutes(path: EducationalPathUserDTO): number {
    const required = path.maintenance_minutes_required || 0;
    if (required <= 0) {
      return 0;
    }
    return (this.getMaintenanceIntendedPercentage(path) / 100) * required;
  }

  /**
   * Calculates the extra percentage for signed-up but not yet attended events (orange bar segment).
   *
   * Primary: maintenance minutes from in-progress path items (see getMaintenanceInProgressMinutesEffective).
   * Fallback: registered_percentage from the API minus the already-completed percentage.
   * Both are capped so green + orange never exceed 100 %.
   */
  getMaintenanceIntendedPercentage(path: EducationalPathUserDTO): number {
    if (
      !path.maintenance_minutes_required ||
      path.maintenance_minutes_required === 0
    ) {
      return 0;
    }
    const completed = this.getMaintenanceProgressPercentage(path);
    const remaining = 100 - completed;

    const inProgressMinutes =
      this.getMaintenanceInProgressMinutesEffective(path);

    if (inProgressMinutes > 0) {
      const inProgressPct =
        (inProgressMinutes / path.maintenance_minutes_required) * 100;
      return Math.max(0, Math.min(inProgressPct, remaining));
    }

    if (this.countModuleOnce) {
      return 0;
    }

    // --- Fallback: registered_percentage from API (minus "Allerede talt med" signups) ---
    const registeredPct = path.registered_percentage ?? 0;
    if (registeredPct > completed) {
      let extra = Math.max(0, Math.min(registeredPct - completed, remaining));
      const phantomPct =
        path.maintenance_minutes_required > 0
          ? Math.min(
              remaining,
              (this.getDedupedFullOverlapParticipationMaintenanceMinutes(path) /
                path.maintenance_minutes_required) *
                100
            )
          : 0;
      extra = Math.max(0, extra - phantomPct);
      return extra;
    }

    return 0;
  }

  private isPathItemRequired(
    item: EducationalPathItemUserDTO | Record<string, unknown>
  ): boolean {
    const record = item as Record<string, unknown>;
    const isRequired = record['is_required'];
    if (isRequired === true || isRequired === 1 || isRequired === '1') {
      return true;
    }
    if (
      isRequired === false ||
      isRequired === 0 ||
      isRequired === '0' ||
      isRequired === 'false' ||
      isRequired === 'f'
    ) {
      return false;
    }
    if (isRequired === 'true' || isRequired === 't') {
      return true;
    }
    const required = record['required'];
    if (required === true || required === 1 || required === '1') {
      return true;
    }
    if (
      required === false ||
      required === 0 ||
      required === '0' ||
      required === 'false'
    ) {
      return false;
    }
    if (required === 'true' || required === 't') {
      return true;
    }
    return false;
  }

  private getPathItemModuleId(
    item: EducationalPathItemUserDTO | Record<string, unknown>
  ): number | null {
    const record = item as Record<string, unknown>;
    const itemDetails = record['item_details'] as Record<string, unknown> | undefined;
    const candidates = [
      record['item_id'],
      record['module_id'],
      itemDetails?.['module_id'],
      itemDetails?.['id'],
      record['id'],
    ];
    for (const raw of candidates) {
      if (raw == null || raw === '') {
        continue;
      }
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
    return null;
  }

  /** Resolve duna_modules.module_id for calendar deeplink (incl. completed / manual modules). */
  getModuleCalendarModuleId(
    path: EducationalPathUserDTO,
    module: ModuleItem
  ): number | null {
    if (module.moduleId != null) {
      return module.moduleId;
    }

    const nameKey = module.name.trim().toLowerCase();
    const pathItems = [
      ...this.ensureCompatibleItemFormat(path.requirements || [], true),
      ...this.ensureCompatibleItemFormat(path.maintenance || [], false),
    ];
    for (const item of pathItems) {
      const itemName = String((item as { name?: string }).name ?? '')
        .trim()
        .toLowerCase();
      if (itemName === nameKey) {
        const id = this.getPathItemModuleId(item);
        if (id != null) {
          return id;
        }
      }
    }

    const manualMatch = (path.manual_completed_modules ?? []).find(
      (entry) =>
        entry?.module_name &&
        entry.module_name.trim().toLowerCase() === nameKey
    );
    if (manualMatch?.module_id) {
      return manualMatch.module_id;
    }

    const pathModuleIds = new Set<number>();
    for (const item of pathItems) {
      const id = this.getPathItemModuleId(item);
      if (id != null) {
        pathModuleIds.add(id);
      }
    }
    for (const event of path.participation_events ?? []) {
      for (const id of this.getParticipationEventModuleIds(event)) {
        if (pathModuleIds.has(id)) {
          const matchedItem = pathItems.find(
            (item) => this.getPathItemModuleId(item) === id
          );
          const matchedName = String(
            (matchedItem as { name?: string } | undefined)?.name ?? ''
          )
            .trim()
            .toLowerCase();
          if (matchedName === nameKey) {
            return id;
          }
        }
      }
    }

    return null;
  }

  getModuleCalendarQueryParams(moduleId: number): { mods: number } {
    return { mods: moduleId };
  }

  private getPathItemMaintenanceMinutes(
    item: EducationalPathItemUserDTO
  ): number {
    return Number(
      (item as any).item_details?.maintenance_minutes ??
        (item as any).maintenance_minutes ??
        0
    );
  }

  /** Module IDs that appear on this path as maintenance items. */
  private getPathMaintenanceModuleIdSet(
    path: EducationalPathUserDTO
  ): Set<number> {
    const items = this.ensureCompatibleItemFormat(
      path.maintenance || [],
      false
    );
    const s = new Set<number>();
    for (const item of items) {
      const mid = this.getPathItemModuleId(item);
      if (mid != null) {
        s.add(mid);
      }
    }
    return s;
  }

  /**
   * Maintenance modules credited within the current profile roll (API items + completed
   * participation events in the roll, so multi-year rolls stay consistent across profile years).
   */
  private getCompletedMaintenanceModuleIds(
    path: EducationalPathUserDTO
  ): Set<number> {
    const pathMods = this.getPathMaintenanceModuleIdSet(path);
    const s = new Set<number>();
    const items = this.ensureCompatibleItemFormat(
      path.maintenance || [],
      false
    );
    for (const item of items) {
      const mid = this.getPathItemModuleId(item);
      if (mid != null && (item.completed || item.status === 'completed')) {
        s.add(mid);
      }
    }
    const rollBounds = this.getProfileRollBounds(path);
    const events = path.participation_events || [];
    for (const event of events) {
      if (!dateInRoll(event.date, rollBounds)) {
        continue;
      }
      if (
        !isParticipationCompletedStatus(this.getParticipationRawStatus(event))
      ) {
        continue;
      }
      for (const id of this.getParticipationEventModuleIds(event)) {
        if (pathMods.has(id)) {
          s.add(id);
        }
      }
    }
    const manualIds = this.getManualCompletedModuleIdSet(path);
    manualIds.forEach((id) => {
      if (pathMods.has(id)) {
        s.add(id);
      }
    });
    return s;
  }

  private getParticipationEventModuleIds(
    event: ParticipationEventDTO
  ): number[] {
    const raw = event.module_ids;
    if (!raw || !Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  /**
   * True when API marks the event as repeating, or when there is an **earlier** participation row
   * on this path that shares a path maintenance module (series-like signup without DB flags).
   */
  private participationEventQualifiesForCreditHint(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): boolean {
    if (this.participationEventIsRepeatingFromApi(event)) {
      return true;
    }
    return this.participationEventSharesPathModuleWithOtherSignup(path, event);
  }

  /** Accept bool or common JSON string forms; supports optional camelCase from intermediaries. */
  private participationEventIsRepeatingFromApi(
    event: ParticipationEventDTO
  ): boolean {
    const e = event as ParticipationEventDTO & { isRepeating?: boolean };
    const v: unknown = e.is_repeating ?? e.isRepeating;
    if (v === true || v === 1) {
      return true;
    }
    if (v === false || v === 0) {
      return false;
    }
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === 'true' || s === '1' || s === 't' || s === 'yes';
    }
    return false;
  }

  private parseParticipationEventDateMs(event: ParticipationEventDTO): number {
    const d = event?.date ? Date.parse(String(event.date)) : NaN;
    return Number.isFinite(d) ? d : 0;
  }

  /** Strict ordering for "first" vs "later" occurrence (date, then uuid). */
  private participationEventIsStrictlyBeforeOther(
    a: ParticipationEventDTO,
    b: ParticipationEventDTO
  ): boolean {
    const ta = this.parseParticipationEventDateMs(a);
    const tb = this.parseParticipationEventDateMs(b);
    if (ta !== tb) {
      return ta < tb;
    }
    return a.uuid.localeCompare(b.uuid) < 0;
  }

  /**
   * Another signup on this path shares a maintenance module, and that signup is **earlier** than
   * this row — within the **same UI scope as the event list** (selected year), not all-time.
   * Using full `path.participation_events` made every row in the filtered year look "later" than
   * older years’ signups, so almost every visible event qualified for the badge.
   */
  private participationEventSharesPathModuleWithOtherSignup(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): boolean {
    const list = this.getPathParticipationEvents(path);
    if (!list || list.length < 2) {
      return false;
    }
    const pathMods = this.getPathMaintenanceModuleIdSet(path);
    const evMods = new Set(
      this.getParticipationEventModuleIds(event).filter((id) =>
        pathMods.has(id)
      )
    );
    if (evMods.size === 0) {
      return false;
    }
    for (const other of list) {
      if (other.uuid === event.uuid) {
        continue;
      }
      const oMods = this.getParticipationEventModuleIds(other).filter((id) =>
        pathMods.has(id)
      );
      if (!oMods.some((id) => evMods.has(id))) {
        continue;
      }
      if (this.participationEventIsStrictlyBeforeOther(other, event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Overlap between this event's path-relevant modules and modules already credited on the path.
   * Used to label events that will not add (more) maintenance for those modules.
   */
  getParticipationEventCreditOverlap(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): 'full' | 'partial' | null {
    if (!this.participationEventQualifiesForCreditHint(path, event)) {
      return null;
    }
    const pathMods = this.getPathMaintenanceModuleIdSet(path);
    const evMods = this.getParticipationEventModuleIds(event).filter((id) =>
      pathMods.has(id)
    );
    if (evMods.length === 0) {
      return null;
    }
    const done = this.getCompletedMaintenanceModuleIds(path);
    const credited = evMods.filter((id) => done.has(id));
    if (credited.length === 0) {
      return null;
    }
    return credited.length >= evMods.length ? 'full' : 'partial';
  }

  getParticipationEventCreditLabel(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): string | null {
    const o = this.getParticipationEventCreditOverlap(path, event);
    if (o === 'full') {
      return 'Allerede talt med';
    }
    if (o === 'partial') {
      return 'Delvist talt med';
    }
    return null;
  }

  getParticipationEventCreditTooltip(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): string | null {
    const o = this.getParticipationEventCreditOverlap(path, event);
    if (o === 'full') {
      return 'Event indeholder kun vedligeholdelsesmoduler, du allerede har gennemført på denne sti — tilmelding giver ikke ekstra vedligeholdelsestimer her.';
    }
    if (o === 'partial') {
      return 'Event indeholder mindst ét vedligeholdelsesmodul, du allerede har talt med på denne sti — ekstra timer kan være begrænsede.';
    }
    return null;
  }

  /**
   * Kort tekst med summen af vedligeholdelsesminutter for denne sti (kun moduler på stiens vedligehold,
   * som også findes på eventet). Tom når alt er "Allerede talt med" eller der ikke er minutter at vise.
   */
  getParticipationEventMaintenanceMinutesText(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): string | null {
    if (this.getParticipationEventCreditOverlap(path, event) === 'full') {
      return null;
    }
    const total = this.sumParticipationEventPathMaintenanceMinutes(path, event);
    if (total <= 0) {
      return null;
    }
    return `${total} min.`;
  }

  getParticipationEventMaintenanceMinutesTooltip(): string {
    return 'Vedligeholdelsesminutter for denne uddannelsessti fra moduler på stiens vedligehold, som dette event indeholder.';
  }

  /**
   * Maintenance modules on this path that the event covers (what the user can gain on this sti).
   */
  getParticipationEventPathModules(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): { name: string; minutes: number }[] {
    const pathModSet = this.getPathMaintenanceModuleIdSet(path);
    const items = this.ensureCompatibleItemFormat(
      path.maintenance || [],
      false
    );
    const seen = new Set<number>();
    const rows: { name: string; minutes: number }[] = [];

    for (const id of this.getParticipationEventModuleIds(event)) {
      if (!pathModSet.has(id) || seen.has(id)) {
        continue;
      }
      seen.add(id);
      const item = items.find((row) => this.getPathItemModuleId(row) === id);
      let name = String((item as { name?: string } | undefined)?.name ?? '').trim();
      if (!name) {
        for (const mod of [
          ...this.getRequiredModules(path),
          ...this.getOptionalModules(path),
        ]) {
          if (Number(mod.id) === id) {
            name = mod.name;
            break;
          }
        }
      }
      if (!name) {
        name = `Modul ${id}`;
      }
      const minutes = item ? this.getPathItemMaintenanceMinutes(item) : 0;
      rows.push({ name, minutes });
    }

    rows.sort((a, b) => a.name.localeCompare(b.name, 'da'));
    return rows;
  }

  /** Summér path.maintenance-minutter for et sæt modul-id'er på stien. */
  private sumPathMaintenanceMinutesForModuleIds(
    path: EducationalPathUserDTO,
    moduleIds: Set<number>
  ): number {
    if (moduleIds.size === 0) {
      return 0;
    }
    const items = this.ensureCompatibleItemFormat(
      path.maintenance || [],
      false
    );
    let total = 0;
    for (const mid of moduleIds) {
      for (const item of items) {
        if (this.getPathItemModuleId(item) === mid) {
          total += this.getPathItemMaintenanceMinutes(item);
          break;
        }
      }
    }
    return total;
  }

  /**
   * Unik sum af vedligeholdelsesminutter for moduler der indgår i mindst ét event (valgt år) med
   * fuld overlap ("Allerede talt med"). Tilmelding der ikke giver ny effekt skal ikke drive
   * "Tilmeldt"-segmentet i vedligeholdelsestiden.
   */
  private getDedupedFullOverlapParticipationMaintenanceMinutes(
    path: EducationalPathUserDTO
  ): number {
    const pathModSet = this.getPathMaintenanceModuleIdSet(path);
    const moduleIds = new Set<number>();
    for (const event of this.getPathParticipationEvents(path)) {
      if (this.getParticipationEventCreditOverlap(path, event) !== 'full') {
        continue;
      }
      for (const id of this.getParticipationEventModuleIds(event)) {
        if (pathModSet.has(id)) {
          moduleIds.add(id);
        }
      }
    }
    return this.sumPathMaintenanceMinutesForModuleIds(path, moduleIds);
  }

  /** Summér minutter fra path.maintenance-rækker matchet på modul-id og event.module_ids. */
  private sumParticipationEventPathMaintenanceMinutes(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): number {
    const pathModSet = this.getPathMaintenanceModuleIdSet(path);
    const fromEvent = new Set(
      this.getParticipationEventModuleIds(event).filter((id) =>
        pathModSet.has(id)
      )
    );
    return this.sumPathMaintenanceMinutesForModuleIds(path, fromEvent);
  }

  // Make a temp fix to handle old data structure while the API is being updated
  private ensureCompatibleItemFormat(
    items: any[],
    isFromRequirementsArray: boolean = false
  ): EducationalPathItemUserDTO[] {
    if (!items || !Array.isArray(items)) return [];

    return items.map((item) => {
      // Ensure we have an item to work with
      if (!item) return item;

      // Make sure the is_required field exists
      if (item.is_required === undefined) {
        // If the item has a type field that says 'requirement', it's required
        if (item.type === 'requirement') {
          item.is_required = true;
        }
        // If the item has a required field, use that
        else if (item.required !== undefined) {
          item.is_required = !!item.required;
        }
        // Default to the source array - requirements array items are required by default
        else {
          item.is_required = isFromRequirementsArray;
        }
      }

      // Make sure id/guid fields are in the expected formats
      if (!item.id && item.item_id) {
        item.id = item.item_id;
      }

      if (!item.id && !item.item_id && !item.guid) {
        item.guid = 'id-' + Math.random().toString(36).substr(2, 9);
      }

      // Default status if missing
      if (!item.status) {
        if (item.completed) {
          item.status = 'completed';
        } else {
          item.status = 'not_started';
        }
      }

      return item;
    });
  }

  private sortModulesByName(modules: ModuleItem[]): ModuleItem[] {
    return modules.sort((a, b) =>
      a.name.localeCompare(b.name, 'da', { sensitivity: 'base', numeric: true })
    );
  }

  /**
   * Returns required modules for a path
   */
  getRequiredModules(path: EducationalPathUserDTO): ModuleItem[] {
    // Use cached modules if available - cache key must include the selected year
    const selectedYear = this.selectedYear$.getValue();
    const cacheKey = `${this.getModuleCacheUserKey()}-${path.guid || ''}-${
      path.path_id || ''
    }-${selectedYear}-required`;
    if (this.requiredModulesCache[cacheKey]) {
      return this.requiredModulesCache[cacheKey];
    }

    // Extract required modules from path
    const requiredModules: ModuleItem[] = [];

    // Ensure requirements and maintenance arrays exist
    const requirements = this.ensureCompatibleItemFormat(
      path.requirements || [],
      true
    );
    const maintenance = this.ensureCompatibleItemFormat(
      path.maintenance || [],
      false
    );

    // Debug output to see what data we're working with

    // Combine all modules from requirements and maintenance arrays
    const allModules = [...requirements, ...maintenance];

    // Filter required modules based on is_required field
    const requiredItems = allModules.filter((item: EducationalPathItemUserDTO) =>
      this.isPathItemRequired(item)
    );

    if (requiredItems && Array.isArray(requiredItems)) {
      requiredItems.forEach((item: any) => {
        const moduleId =
          item.id?.toString() ||
          item.item_id?.toString() ||
          item.guid ||
          Math.random().toString();
        const descriptions = this.extractDescriptions(item);

        const moduleIdNum = this.getPathItemModuleId(item);
        const creditedInRoll =
          moduleIdNum != null &&
          this.getCompletedMaintenanceModuleIds(path).has(moduleIdNum);
        const moduleStatus = this.getModuleStatus(item, creditedInRoll);

        // Extract maintenance minutes from item_details or directly from item
        const maintenanceMinutes = Number(
          (item as any).item_details?.maintenance_minutes ||
            item.maintenance_minutes ||
            0
        );

        // Debug logging to help diagnose maintenance minutes
        if (item.completed) {
        }

        const participation = this.resolveModuleParticipationFromEvents(
          path,
          moduleIdNum,
          item
        );

        let moduleItem: ModuleItem = {
          id: moduleId,
          moduleId: moduleIdNum,
          name: item.name || 'Unavngivet modul',
          minutes: Number(item.minutes || 0),
          required: true,
          completed: Boolean(
            item.completed || item.status === 'completed' || creditedInRoll
          ),
          descriptions: descriptions,
          expanded: this.expandedModules[moduleId] || false,
          status: moduleStatus,
          completed_at: item.completed_at || null,
          participation_dates: participation.dates,
          participation_event_sources: participation.sources,
          maintenance_minutes: maintenanceMinutes,
        };
        moduleItem = this.applyManualCompletionToModule(
          path,
          moduleIdNum,
          true,
          moduleItem
        );
        requiredModules.push(moduleItem);
      });
    }

    this.sortModulesByName(requiredModules);
    this.requiredModulesCache[cacheKey] = requiredModules;
    return requiredModules;
  }

  /**
   * Returns optional modules for a path
   */
  getOptionalModules(path: EducationalPathUserDTO): ModuleItem[] {
    // Use cached modules if available - cache key must include the selected year
    const selectedYear = this.selectedYear$.getValue();
    const cacheKey = `${this.getModuleCacheUserKey()}-${path.guid || ''}-${
      path.path_id || ''
    }-${selectedYear}-optional`;
    if (this.optionalModulesCache[cacheKey]) {
      return this.optionalModulesCache[cacheKey];
    }

    // Extract optional modules from path
    const optionalModules: ModuleItem[] = [];

    // Ensure requirements and maintenance arrays exist
    const requirements = this.ensureCompatibleItemFormat(
      path.requirements || [],
      true
    );
    const maintenance = this.ensureCompatibleItemFormat(
      path.maintenance || [],
      false
    );

    // Combine all modules from requirements and maintenance arrays
    const allModules = [...requirements, ...maintenance];

    // Filter optional modules (explicitly not required)
    const optionalItems = allModules.filter(
      (item: EducationalPathItemUserDTO) => !this.isPathItemRequired(item)
    );

    if (optionalItems && Array.isArray(optionalItems)) {
      optionalItems.forEach((item: any) => {
        const moduleId =
          item.id?.toString() ||
          item.item_id?.toString() ||
          item.guid ||
          Math.random().toString();
        const descriptions = this.extractDescriptions(item);

        const moduleIdNum = this.getPathItemModuleId(item);
        const creditedInRoll =
          moduleIdNum != null &&
          this.getCompletedMaintenanceModuleIds(path).has(moduleIdNum);
        const moduleStatus = this.getModuleStatus(item, creditedInRoll);

        // Extract maintenance minutes from item_details or directly from item
        const maintenanceMinutes = Number(
          (item as any).item_details?.maintenance_minutes ||
            item.maintenance_minutes ||
            0
        );

        // Debug logging to help diagnose maintenance minutes
        if (item.completed) {
        }

        const participation = this.resolveModuleParticipationFromEvents(
          path,
          moduleIdNum,
          item
        );

        let moduleItem: ModuleItem = {
          id: moduleId,
          moduleId: moduleIdNum,
          name: item.name || 'Unavngivet modul',
          minutes: Number(item.minutes || 0),
          required: false,
          completed: Boolean(
            item.completed || item.status === 'completed' || creditedInRoll
          ),
          descriptions: descriptions,
          expanded: this.expandedModules[moduleId] || false,
          status: moduleStatus,
          completed_at: item.completed_at || null,
          participation_dates: participation.dates,
          participation_event_sources: participation.sources,
          maintenance_minutes: maintenanceMinutes,
        };
        moduleItem = this.applyManualCompletionToModule(
          path,
          moduleIdNum,
          false,
          moduleItem
        );
        optionalModules.push(moduleItem);
      });
    }

    //Show modules in console

    this.sortModulesByName(optionalModules);
    this.optionalModulesCache[cacheKey] = optionalModules;
    return optionalModules;
  }

  /**
   * Extract descriptions from module item.
   *
   * The API returns descriptions in two different shapes depending on code path:
   *   1. Sequential array: [{language_code: 'da', translation_text: '...'}, ...]
   *   2. Object keyed by language: {da: {language_code:'da', translation_text:'...'}, en:{...}}
   *      (comes from ModuleDTO which uses PHP associative arrays serialised as JSON objects)
   *
   * Both are handled here, at both the top level and inside item_details.
   */
  private extractDescriptions(item: any): ModuleDescription[] {
    const raw: { language_code: string; text: string }[] = [];

    const pushDesc = (langCode: any, text: any) => {
      const t = String(text ?? '').trim();
      if (t) raw.push({ language_code: String(langCode ?? ''), text: t });
    };

    // Helper: iterate descriptions regardless of array vs object format
    const iterateDescs = (descs: any) => {
      if (!descs) return;
      if (Array.isArray(descs)) {
        descs.forEach((d: any) => {
          if (d?.translation_text !== undefined)
            pushDesc(d.language_code, d.translation_text);
          else if (d?.description !== undefined)
            pushDesc(d.language_id ?? d.language_code, d.description);
        });
      } else if (typeof descs === 'object') {
        // Object keyed by language code: {da: {translation_text:'...'}, en: {...}}
        Object.entries(descs).forEach(([langKey, d]: [string, any]) => {
          if (typeof d === 'object' && d !== null) {
            pushDesc(
              d.language_code ?? langKey,
              d.translation_text ?? d.description ?? ''
            );
          } else if (typeof d === 'string') {
            pushDesc(langKey, d);
          }
        });
      }
    };

    // 1. Direct string description
    if (item.description) pushDesc('0', item.description);

    // 2. descriptions at top level (array or keyed object)
    iterateDescs(item.descriptions);

    // 3. Nested in item_details
    if (item.item_details) {
      if (item.item_details.description)
        pushDesc('0', item.item_details.description);
      iterateDescs(item.item_details.descriptions);
    }

    // 4. Nested in data
    if (item.data) {
      if (item.data.description) pushDesc('0', item.data.description);
      iterateDescs(item.data.descriptions);
    }

    // Deduplicate by language_code + text
    const seen = new Set<string>();
    return raw
      .filter((d) => {
        const key = `${d.language_code}:${d.text}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(
        (d) =>
          ({
            language_id: Number(d.language_code) || 0,
            // Store the original language_code string so getModuleDescription can pick Danish
            language_code: d.language_code,
            description: d.text,
          } as ModuleDescription & { language_code?: string })
      );
  }

  /**
   * Match path participation events to a module within the current maintenance roll.
   */
  private resolveModuleParticipationFromEvents(
    path: EducationalPathUserDTO,
    moduleId: number | null,
    item: Record<string, unknown>
  ): { dates: string[]; sources: ModuleEventSource[] } {
    const sources: ModuleEventSource[] = [];
    const dateKeys = new Set<string>();

    const addDate = (d: unknown) => {
      const s = String(d ?? '').trim();
      if (s) {
        dateKeys.add(s);
      }
    };

    const rawDates = item['participation_dates'];
    if (Array.isArray(rawDates)) {
      rawDates.forEach(addDate);
    }
    addDate(item['completed_at']);

    if (moduleId == null || !path.participation_events?.length) {
      return {
        dates: this.sortParticipationDateStrings([...dateKeys]),
        sources,
      };
    }

    const rollBounds = this.getProfileRollBounds(path);
    const creditedInRoll =
      moduleId != null &&
      this.getCompletedMaintenanceModuleIds(path).has(moduleId);
    const itemCompleted = Boolean(
      item['completed'] ||
        item['status'] === 'completed' ||
        creditedInRoll
    );
    const itemInProgress =
      !itemCompleted &&
      (item['status'] === 'in_progress' ||
        item['status'] === 'pending' ||
        item['status'] === 'waitlist' ||
        item['status'] === 'shownup');

    if (!itemCompleted && !itemInProgress) {
      return {
        dates: this.sortParticipationDateStrings([...dateKeys]),
        sources,
      };
    }

    const matchingEvents = path.participation_events.filter((event) => {
      if (!event.date) {
        return false;
      }
      if (!dateInRoll(event.date, rollBounds)) {
        return false;
      }
      return this.getParticipationEventModuleIds(event).includes(moduleId);
    });

    const pushEvent = (event: ParticipationEventDTO) => {
      const rawStatus = this.getParticipationRawStatus(event);
      sources.push({
        date: event.date,
        name: event.name || 'Event',
        status: rawStatus,
      });
      addDate(event.date);
    };

    const statusMatchesModule = (rawStatus: string): boolean => {
      if (itemCompleted) {
        return (
          rawStatus === 'completed' ||
          rawStatus === 'shownup' ||
          rawStatus === 'attended'
        );
      }
      if (itemInProgress) {
        return (
          rawStatus === 'pending' ||
          rawStatus === 'confirmed' ||
          rawStatus === 'waitlist' ||
          rawStatus === 'shownup'
        );
      }
      return false;
    };

    for (const event of matchingEvents) {
      if (statusMatchesModule(this.getParticipationRawStatus(event))) {
        pushEvent(event);
      }
    }

    if (sources.length === 0 && matchingEvents.length > 0) {
      for (const event of matchingEvents) {
        pushEvent(event);
      }
    }

    sources.sort(
      (a, b) =>
        this.parseParticipationEventDateMs({
          date: a.date,
        } as ParticipationEventDTO) -
        this.parseParticipationEventDateMs({
          date: b.date,
        } as ParticipationEventDTO)
    );

    return { dates: this.sortParticipationDateStrings([...dateKeys]), sources };
  }

  private sortParticipationDateStrings(dates: string[]): string[] {
    return [...dates].sort(
      (a, b) =>
        this.parseParticipationEventDateMs({
          date: a,
        } as ParticipationEventDTO) -
        this.parseParticipationEventDateMs({ date: b } as ParticipationEventDTO)
    );
  }

  /**
   * Get participation dates for a module
   */
  getParticipationDates(module: ModuleItem): string[] {
    if (module.participation_dates && module.participation_dates.length > 0) {
      return this.sortParticipationDateStrings(module.participation_dates);
    }

    if (module.completed && module.completed_at) {
      return [module.completed_at];
    }

    return [];
  }

  private compareParticipationSourceDatesAsc(
    a: ModuleEventSource,
    b: ModuleEventSource
  ): number {
    return (
      this.parseParticipationEventDateMs({
        date: a.date,
      } as ParticipationEventDTO) -
      this.parseParticipationEventDateMs({
        date: b.date,
      } as ParticipationEventDTO)
    );
  }

  /** Primary event row: earliest relevant participation in the current profile roll. */
  private getModulePrimaryEventSource(
    module: ModuleItem
  ): ModuleEventSource | null {
    const sources = module.participation_event_sources;
    if (sources?.length) {
      const byDateAsc = [...sources].sort((a, b) =>
        this.compareParticipationSourceDatesAsc(a, b)
      );
      if (module.status === 'in_progress') {
        const enrolled = byDateAsc.filter((s) =>
          isParticipationCompletedStatus(s.status)
            ? false
            : ['pending', 'confirmed', 'waitlist', 'shownup'].includes(
                (s.status ?? '').toLowerCase()
              )
        );
        if (enrolled.length) {
          return enrolled[0];
        }
        return byDateAsc[0];
      }
      const attended = byDateAsc.filter((s) =>
        isParticipationCompletedStatus(s.status)
      );
      if (attended.length) {
        return attended[0];
      }
      return byDateAsc[0];
    }

    const dates = this.getParticipationDates(module);
    if (dates.length) {
      const sorted = this.sortParticipationDateStrings(dates);
      return { date: sorted[0], name: '', status: '' };
    }
    return null;
  }

  /** Whether to show an event date on the module row (completed = green, in progress = orange). */
  showModuleEventDate(module: ModuleItem): boolean {
    return (
      (module.status === 'completed' || module.status === 'in_progress') &&
      this.getModulePrimaryEventSource(module) != null
    );
  }

  getModuleEventDate(module: ModuleItem): string | null {
    return this.getModulePrimaryEventSource(module)?.date ?? null;
  }

  getModuleEventDateTooltip(module: ModuleItem): string {
    const sources = module.participation_event_sources;
    if (sources?.length) {
      return sources
        .map((s) => {
          const d = new Date(s.date).toLocaleDateString('da-DK');
          return s.name ? `${s.name} (${d})` : d;
        })
        .join('\n');
    }
    const dates = this.getParticipationDates(module);
    if (dates.length) {
      return dates
        .map((d) => new Date(d).toLocaleDateString('da-DK'))
        .join('\n');
    }
    return 'Eventdato for dette modul';
  }

  getModuleEventDateLabel(module: ModuleItem): string {
    if (module.status === 'completed') {
      return 'Deltaget';
    }
    if (module.status === 'in_progress') {
      return 'Tilmeldt';
    }
    return 'Event';
  }

  /**
   * Vedligeholdelse/minutter modulet giver ved deltagelse (alle moduler, uanset status).
   */
  getModuleMaintenanceMinutes(module: ModuleItem): number {
    const fromMaintenance = module.maintenance_minutes;
    if (fromMaintenance !== undefined && fromMaintenance !== null) {
      return Math.max(0, Number(fromMaintenance) || 0);
    }
    return Math.max(0, Number(module.minutes) || 0);
  }

  /** Human-readable maintenance credit for module cards. */
  getModuleMaintenanceCreditText(module: ModuleItem): string {
    const mins = this.getModuleMaintenanceMinutes(module);
    if (mins <= 0) {
      return 'Ingen vedligeholdelse ved deltagelse';
    }
    if (mins >= 60) {
      const hours = mins / 60;
      const text =
        Math.abs(hours - Math.round(hours)) < 0.05
          ? String(Math.round(hours))
          : hours.toFixed(1).replace('.', ',');
      return `${text} t ved deltagelse`;
    }
    return `${mins} min. ved deltagelse`;
  }

  hasModuleDetailPanel(
    path: EducationalPathUserDTO,
    module: ModuleItem
  ): boolean {
    return (
      !!this.getModuleDescription(module).trim() ||
      this.getModuleCalendarModuleId(path, module) != null
    );
  }

  /**
   * Get participation events for a specific educational path, filtered to the selected year
   * Only returns real participation events from the backend API
   */
  getPathParticipationEvents(
    path: EducationalPathUserDTO
  ): ParticipationEventDTO[] {
    if (
      !path.participation_events ||
      !Array.isArray(path.participation_events) ||
      path.participation_events.length === 0
    ) {
      return [];
    }

    const rollBounds = this.getProfileRollBounds(path);
    return path.participation_events.filter((event) =>
      dateInRoll(event.date, rollBounds)
    );
  }

  /**
   * Generate a temporary UUID for events that don't have one
   * This is a simple implementation and should be replaced with a proper UUID generator
   */
  private generateTempUuid(): string {
    return (
      'temp-' +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  // Add helper method for loading state
  shouldShowLoading(paths: EducationalPathUserDTO[] | null): boolean {
    return this.isLoading$.value || !paths;
  }

  shouldShowContent(paths: EducationalPathUserDTO[] | null): boolean {
    return !this.isLoading$.value && !!paths;
  }

  // Add this new helper method to normalize the paths data structure
  private normalizePaths(paths: any[]): EducationalPathUserDTO[] {
    // Handle null or undefined paths
    if (!paths) {
      return [];
    }

    if (!Array.isArray(paths)) {
      paths = paths ? [paths] : [];
    }

    const normalizedPaths: EducationalPathUserDTO[] = [];

    paths.forEach((pathObj) => {
      // Check if this is an object with path IDs as keys
      const hasPathIdKeys =
        typeof pathObj === 'object' &&
        pathObj !== null &&
        !Array.isArray(pathObj) &&
        Object.keys(pathObj).some((key) => !isNaN(parseInt(key)));

      if (hasPathIdKeys) {
        // Extract each path by ID
        Object.keys(pathObj).forEach((pathId) => {
          if (!isNaN(parseInt(pathId))) {
            const path = pathObj[pathId];
            if (path && typeof path === 'object') {
              // Generate a unique ID if needed
              const uniqueId = path.guid || path.path_id || pathId;

              // Ensure it has all necessary properties
              const normalizedPath = {
                ...path,
                path_id: path.path_id || parseInt(pathId),
                guid: path.guid || `path-${uniqueId}`,
                name: this.getDisplayPathName(path.name, path.full_name),
                full_name: path.full_name || path.name || 'Unknown Path',
                requirements: path.requirements || [],
                maintenance: path.maintenance || [],
              };

              normalizedPaths.push(normalizedPath);
            }
          }
        });
      } else if (typeof pathObj === 'object' && pathObj !== null) {
        // If this is a single path object with a path_id
        if (pathObj.path_id || pathObj.name) {
          // Generate a unique ID if needed
          const uniqueId =
            pathObj.guid ||
            pathObj.path_id ||
            Math.random().toString(36).substr(2, 9);

          const normalizedPath = {
            ...pathObj,
            guid: pathObj.guid || `path-${uniqueId}`,
            name: this.getDisplayPathName(pathObj.name, pathObj.full_name),
            full_name: pathObj.full_name || pathObj.name || 'Unknown Path',
            requirements: pathObj.requirements || [],
            maintenance: pathObj.maintenance || [],
          };

          normalizedPaths.push(normalizedPath);
        } else if (pathObj.status && pathObj.status.educational_paths) {
          // Handle nested structure with educational_paths
          const nestedPaths = pathObj.status.educational_paths;
          Object.keys(nestedPaths).forEach((pathId) => {
            if (!isNaN(parseInt(pathId))) {
              const path = nestedPaths[pathId];
              if (path && typeof path === 'object') {
                // Generate a unique ID if needed
                const uniqueId = path.guid || path.path_id || pathId;

                const normalizedPath = {
                  ...path,
                  path_id: path.path_id || parseInt(pathId),
                  guid: path.guid || `path-${uniqueId}`,
                  name: this.getDisplayPathName(path.name, path.full_name),
                  full_name: path.full_name || path.name || 'Unknown Path',
                  requirements: path.requirements || [],
                  maintenance: path.maintenance || [],
                };

                normalizedPaths.push(normalizedPath);
              }
            }
          });
        }
      }
    });

    // Deduplicate paths by path_id - sometimes the API returns duplicates
    const dedupedPaths = normalizedPaths.reduce(
      (acc: EducationalPathUserDTO[], current) => {
        const mergeManualFields = (
          into: EducationalPathUserDTO,
          from: EducationalPathUserDTO
        ): void => {
          into.maintenance_minutes_manual = Math.max(
            Math.max(0, Number(into.maintenance_minutes_manual ?? 0)),
            Math.max(0, Number(from.maintenance_minutes_manual ?? 0))
          );
          const acInto = this.coerceMaintenanceManualAcquireAll(
            into.maintenance_manual_acquire_all
          );
          const acFrom = this.coerceMaintenanceManualAcquireAll(
            from.maintenance_manual_acquire_all
          );
          into.maintenance_manual_acquire_all = acInto || acFrom;
          const mergedModules = new Map<
            number,
            { module_id: number; module_name?: string }
          >();
          for (const entry of [
            ...(into.manual_completed_modules ?? []),
            ...(from.manual_completed_modules ?? []),
          ]) {
            const id = Number(entry?.module_id);
            if (!Number.isFinite(id) || id <= 0) continue;
            mergedModules.set(id, {
              module_id: id,
              module_name:
                entry?.module_name ?? mergedModules.get(id)?.module_name,
            });
          }
          if (mergedModules.size > 0) {
            into.manual_completed_modules = Array.from(mergedModules.values());
          }
        };

        const existingPath = acc.find((p) => p.path_id === current.path_id);
        if (!existingPath) {
          acc.push(current);
        } else {
          mergeManualFields(existingPath, current);
          // If this path has more data than the existing one, use this one instead
          if (
            current.requirements?.length > existingPath.requirements?.length ||
            current.maintenance?.length > existingPath.maintenance?.length ||
            (current.full_name && !existingPath.full_name)
          ) {
            const index = acc.indexOf(existingPath);
            mergeManualFields(current, existingPath);
            acc[index] = current;
          }
        }
        return acc;
      },
      []
    );

    return dedupedPaths;
  }

  /**
   * Check if the path has participation from the specified year
   * @param path The educational path to check
   * @param year The year to check for participation
   * @returns True if the path has participation from the specified year
   */
  private hasLastYearParticipation(
    path: EducationalPathUserDTO,
    year: number
  ): boolean {
    // Check participation events first
    const events =
      path.participation_events || this.getPathParticipationEvents(path);

    if (events && events.length > 0) {
      // Check if any events are from the specified year
      for (const event of events) {
        if (event.date) {
          const eventDate = new Date(event.date);
          if (eventDate.getFullYear() === year) {
            return true;
          }
        }
      }
    }

    // Check if maintenance was started in the specified year
    if (path.maintenance_started_at) {
      const startedDate = new Date(path.maintenance_started_at);
      if (startedDate.getFullYear() === year) {
        return true;
      }
    }

    return false;
  }

  /**
   * Determines the current year status of an educational path
   * @param path The educational path to check
   * @returns The current year status
   */
  getCurrentYearStatus(path: EducationalPathUserDTO): CurrentYearStatus {
    const currentStatus = this.getCurrentStatus(path);

    switch (currentStatus) {
      case 'completed':
        return 'completed';
      case 'in_progress':
        return 'in-progress';
      case 'not_started':
      default:
        return 'not-started';
    }
  }

  /**
   * Gets display text for current year status
   * @param status The current year status
   * @returns User-friendly status text
   */
  getCurrentYearStatusText(
    status: CurrentYearStatus,
    path?: EducationalPathUserDTO
  ): string {
    const periodLabel =
      path?.recurrence_type === 'per_user'
        ? this.getMaintenancePeriodRangeText(path) ??
          String(this.getProfileYear())
        : String(this.getProfileYear());
    switch (status) {
      case 'completed':
        return path?.recurrence_type === 'per_user'
          ? 'Gennemført i perioden'
          : `Gennemført ${periodLabel}`;
      case 'in-progress':
        return path?.recurrence_type === 'per_user'
          ? 'Igangværende i perioden'
          : `Igangværende ${periodLabel}`;
      case 'not-started':
        return path?.recurrence_type === 'per_user'
          ? 'Ikke påbegyndt i perioden'
          : `Ikke påbegyndt ${periodLabel}`;
      default:
        return `Status ${periodLabel}`;
    }
  }

  /** Label for "Status for …" row (calendar year vs maintenance period). */
  getProfileStatusScopeLabel(path: EducationalPathUserDTO): string {
    if (path.recurrence_type === 'per_user') {
      return (
        this.getMaintenancePeriodRangeText(path) ??
        `profilår ${this.getProfileYear()}`
      );
    }
    return String(this.getProfileYear());
  }

  /**
   * Gets icon for current year status
   * @param status The current year status
   * @returns Material icon name
   */
  getCurrentYearStatusIcon(status: CurrentYearStatus): string {
    switch (status) {
      case 'completed':
        return 'check_circle';
      case 'in-progress':
        return 'pending';
      case 'not-started':
        return 'schedule';
      default:
        return 'help';
    }
  }

  /**
   * Gets tooltip for current year status
   * @param status The current year status
   * @returns Tooltip text
   */
  getCurrentYearStatusTooltip(status: CurrentYearStatus): string {
    const year = this.getProfileYear();
    switch (status) {
      case 'completed':
        return `Alle påkrævede moduler og vedligeholdelsestimer er gennemført for ${year}`;
      case 'in-progress':
        return `Påbegyndt, men ikke alle moduler/timer er gennemført for ${year}`;
      case 'not-started':
        return `Ingen aktivitet registreret for ${year} endnu`;
      default:
        return `Status for ${year}`;
    }
  }

  /**
   * Gets the DUNA validation status from the backend data
   * @param path The educational path to check
   * @returns The DUNA validation status from backend
   */
  getDunaValidationStatus(path: EducationalPathUserDTO): DunaValidationStatus {
    // Use the validation status provided by the backend for this specific path
    // The backend already calculates this correctly based on previous year data for this path
    return path.validation_status || 'attention';
  }

  /**
   * Calculate the current status from progress data
   * @param path The educational path to check
   * @returns The current status based on progress
   */
  getCurrentStatus(path: EducationalPathUserDTO): string {
    const currentYear = new Date().getFullYear();
    const selectedYear = this.selectedYear$.getValue();

    // If we're looking at the current year, use real-time progress
    if (selectedYear === currentYear) {
      // Check if all required modules are completed for the current year
      const requiredModules = this.getRequiredModules(path);
      const completedRequiredModules = requiredModules.filter(
        (m) => m.completed
      ).length;

      // Check if maintenance requirements are met for the current year
      const maintenanceRequired = path.maintenance_minutes_required || 0;
      const maintenanceCompleted = path.maintenance_minutes_completed || 0;
      const maintenanceComplete =
        maintenanceRequired === 0 ||
        maintenanceCompleted >= maintenanceRequired;

      // Path is completed if:
      // 1. All required modules are done (or there are no required modules) AND
      // 2. Maintenance requirements are met (or there are no maintenance requirements)
      const allRequiredModulesComplete =
        requiredModules.length === 0 ||
        completedRequiredModules === requiredModules.length;

      if (allRequiredModulesComplete && maintenanceComplete) {
        return 'completed';
      }

      // Path is in progress if any modules are completed OR any maintenance time is logged
      if (completedRequiredModules > 0 || maintenanceCompleted > 0) {
        return 'in_progress';
      }

      return 'not_started';
    } else {
      // For historical years, use the overall progress data

      if (
        path.progress_percentage === 100 &&
        path.completed_items === path.total_items
      ) {
        return 'completed';
      } else if (path.progress_percentage > 0 || path.completed_items > 0) {
        return 'in_progress';
      } else {
        return 'not_started';
      }
    }
  }

  /**
   * DB placeholder date used when the real start date is unknown.
   */
  private isPlaceholderPathDate(dateValue: string | null | undefined): boolean {
    if (!dateValue) {
      return false;
    }
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    return (
      date.getFullYear() === 2023 &&
      date.getMonth() === 0 &&
      date.getDate() === 1
    );
  }

  /**
   * Whether the completed-education date should be shown on the profile.
   */
  shouldShowMaintenanceStartDate(path: EducationalPathUserDTO): boolean {
    const date = this.getMaintenanceStartDate(path);
    return !!date && !this.isPlaceholderPathDate(date);
  }

  /**
   * Anchor date for the current maintenance period.
   */
  getMaintenancePeriodAnchor(path: EducationalPathUserDTO): string | null {
    if (path.reset_on_fulfillment && path.recurrence_type === 'per_user') {
      if (path.last_maintenance_fulfilled_at) {
        return path.last_maintenance_fulfilled_at;
      }
      if (
        path.maintenance_started_at &&
        !this.isPlaceholderPathDate(path.maintenance_started_at)
      ) {
        return path.maintenance_started_at;
      }
      return null;
    }

    if (
      path.maintenance_started_at &&
      !this.isPlaceholderPathDate(path.maintenance_started_at)
    ) {
      return path.maintenance_started_at;
    }

    return null;
  }

  /**
   * Last date maintenance requirements were fulfilled (reset-on-fulfillment versions).
   */
  getLastMaintenanceFulfilledDate(path: EducationalPathUserDTO): string | null {
    if (!path.reset_on_fulfillment) {
      return null;
    }
    return path.last_maintenance_fulfilled_at || null;
  }

  /**
   * Gets the date when the user finished/completed the educational path education
   * This is when the user finished the requirements phase (maintenance_started_at)
   * @param path The educational path to check
   * @returns The completion date or null if not available
   */
  getMaintenanceStartDate(path: EducationalPathUserDTO): string | null {
    if (
      path.maintenance_started_at &&
      !this.isPlaceholderPathDate(path.maintenance_started_at)
    ) {
      return path.maintenance_started_at;
    }

    if (
      path.requirement_started_at &&
      !this.isPlaceholderPathDate(path.requirement_started_at)
    ) {
      return path.requirement_started_at;
    }

    if (path.assigned_at && !this.isPlaceholderPathDate(path.assigned_at)) {
      return path.assigned_at;
    }

    return null;
  }

  /**
   * Gets tooltip text for DUNA validation status
   * @param status The DUNA validation status
   * @returns Tooltip text
   */
  getDunaValidationTooltip(
    status: DunaValidationStatus,
    path?: EducationalPathUserDTO
  ): string {
    const periodLabel = path
      ? this.getDunaStatusPeriodLabel(path)
      : String(this.getProfileYear() - 1);

    switch (status) {
      case 'valid':
        return `Uddannelsesstien er gyldig baseret på vedligeholdelse i perioden ${periodLabel}`;
      case 'attention':
        return `Uddannelsesstien kræver opmærksomhed – tjek deltagelse og moduler for perioden ${periodLabel}`;
      case 'no_data':
        return `Ingen data for perioden ${periodLabel} – dette er normalt for nye brugere`;
      case 'no_paths':
        return `Ingen uddannelsesveje tilgængelige for perioden ${periodLabel}`;
      default:
        return `Uddannelsesstien kræver opmærksomhed`;
    }
  }

  /**
   * Check if the user has merit for the current period
   * Merit means they've completed the maintenance requirements for the current renewal period
   * @param path The educational path to check
   * @returns True if user has merit for the current period
   */
  hasMeritForCurrentPeriod(path: EducationalPathUserDTO): boolean {
    const selectedYear = this.getProfileYear();
    const calendarYear = new Date().getFullYear();

    // Get the completion date using the same logic as getMaintenanceStartDate
    const completionDateStr = this.getMaintenanceStartDate(path);
    if (!completionDateStr) {
      return false;
    }

    const completionDate = new Date(completionDateStr);
    completionDate.setHours(0, 0, 0, 0);
    const completionYear = completionDate.getFullYear();

    const recurrenceType = path.recurrence_type || 'calendar';

    if (recurrenceType === 'calendar') {
      const bounds = this.getMaintenancePeriodBoundsForYear(path, selectedYear);
      if (!bounds) {
        return false;
      }
      return (
        completionDate >= bounds.start &&
        completionDate <= bounds.end &&
        completionYear === selectedYear
      );
    }

    if (completionYear !== selectedYear) {
      return false;
    }

    const periodStart = new Date(completionDate);
    periodStart.setHours(0, 0, 0, 0);
    const recurrenceValue = path.recurrence_value;
    const recurrenceUnit = path.recurrence_unit;
    let periodEnd = new Date(periodStart);

    if (recurrenceUnit === 'day' || recurrenceUnit === 'days') {
      periodEnd.setDate(periodEnd.getDate() + (recurrenceValue || 0));
    } else if (recurrenceUnit === 'week' || recurrenceUnit === 'weeks') {
      periodEnd.setDate(periodEnd.getDate() + (recurrenceValue || 0) * 7);
    } else if (recurrenceUnit === 'month' || recurrenceUnit === 'months') {
      periodEnd.setMonth(periodEnd.getMonth() + (recurrenceValue || 0));
    } else if (recurrenceUnit === 'year' || recurrenceUnit === 'years') {
      periodEnd.setFullYear(periodEnd.getFullYear() + (recurrenceValue || 0));
    } else if (recurrenceUnit === 'full_period') {
      periodEnd = new Date('2099-12-31');
    } else {
      periodEnd.setFullYear(periodEnd.getFullYear() + 1);
    }

    let isInPeriod: boolean;
    if (selectedYear === calendarYear) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      isInPeriod = today >= periodStart && today <= periodEnd;
    } else {
      const yearStart = new Date(selectedYear, 0, 1);
      yearStart.setHours(0, 0, 0, 0);
      const yearEnd = new Date(selectedYear, 11, 31, 23, 59, 59);
      isInPeriod = periodStart <= yearEnd && periodEnd >= yearStart;
    }

    return isInPeriod;
  }

  /**
   * Check if an event date is in the future
   * @param eventDate The event date string
   * @returns True if the event is upcoming
   */
  isUpcomingEvent(eventDate: string): boolean {
    const today = new Date();
    const eventDateObj = new Date(eventDate);
    return eventDateObj >= today;
  }

  /** Raw participation status from API (supports legacy `participation_status`). */
  private getParticipationRawStatus(event: ParticipationEventDTO): string {
    const e = event as ParticipationEventDTO & {
      participation_status?: string;
    };
    return String(e.status ?? e.participation_status ?? '')
      .toLowerCase()
      .trim();
  }

  /**
   * CSS class for event list row: completed → green, pending → yellow, waitlist → orange, else red.
   */
  getParticipationEventVisualClass(event: ParticipationEventDTO): string {
    const s = this.getParticipationRawStatus(event);
    if (s === 'completed' || s === 'shownup' || s === 'attended') {
      return 'evt-completed';
    }
    if (s === 'pending' || s === 'confirmed') {
      return 'evt-pending';
    }
    if (s === 'waitlist') {
      return 'evt-waitlist';
    }
    return 'evt-other';
  }

  /** True when the user is signed up but not yet recorded as attended/completed (for the “Kommende” hint). */
  showUpcomingParticipationHint(event: ParticipationEventDTO): boolean {
    if (!this.isUpcomingEvent(event.date)) {
      return false;
    }
    const s = this.getParticipationRawStatus(event);
    return s !== 'completed' && s !== 'shownup' && s !== 'attended';
  }

  /** Label for upcoming events (pill badge, same tone as credit hints). */
  getUpcomingParticipationLabel(): string {
    return 'Kommende deltagelse';
  }

  getUpcomingParticipationTooltip(): string {
    return 'Eventet ligger i fremtiden; din deltagelse er endnu ikke registreret som gennemført.';
  }

  /**
   * True when the path has a non-zero maintenance minute requirement for the period.
   */
  pathRequiresMaintenance(path: EducationalPathUserDTO): boolean {
    return (path.maintenance_minutes_required ?? 0) > 0;
  }

  /**
   * Check if a path has 0 maintenance hours and no required modules
   * @param path The educational path
   * @returns True if the path has 0 maintenance hours and no required modules
   */
  isPathWithNoMaintenanceAndNoRequiredModules(
    path: EducationalPathUserDTO
  ): boolean {
    if (!this.pathRequiresMaintenance(path)) {
      const requiredModules = this.getRequiredModules(path);
      const optionalModules = this.getOptionalModules(path);
      if (requiredModules.length > 0 || optionalModules.length > 0) {
        return false;
      }
      return true;
    }
    const maintenanceRequired = path.maintenance_minutes_required || 0;
    const requirementCount = path.requirements?.length ?? 0;
    const maintenanceCount = path.maintenance?.length ?? 0;
    if (requirementCount > 0 || maintenanceCount > 0) {
      return false;
    }
    const requiredModules = this.getRequiredModules(path);
    const optionalModules = this.getOptionalModules(path);
    return (
      maintenanceRequired === 0 &&
      requiredModules.length === 0 &&
      optionalModules.length === 0
    );
  }

  /**
   * Determines the status of a module based on its data
   * @param item The module item data
   * @returns The status string
   */
  private getModuleStatus(
    item: any,
    creditedInMaintenanceRoll = false
  ): 'completed' | 'in_progress' | 'not_started' {
    if (creditedInMaintenanceRoll) {
      return 'completed';
    }
    // If explicitly completed
    if (item.completed || item.status === 'completed') {
      return 'completed';
    }

    // If has in_progress, pending, waitlist, or shownup status but not completed
    if (
      item.status === 'in_progress' ||
      item.status === 'pending' ||
      item.status === 'waitlist' ||
      item.status === 'shownup'
    ) {
      return 'in_progress';
    }

    // If has participation dates but not completed, consider it in progress
    if (item.participation_dates && item.participation_dates.length > 0) {
      return 'in_progress';
    }

    // If has a started date but not completed
    if (item.started_at && !item.completed) {
      return 'in_progress';
    }

    // Default to not started, but ensure we return a valid status
    const validStatuses: Array<'completed' | 'in_progress' | 'not_started'> = [
      'completed',
      'in_progress',
      'not_started',
    ];
    const itemStatus = item.status as string;

    if (validStatuses.includes(itemStatus as any)) {
      return itemStatus as 'completed' | 'in_progress' | 'not_started';
    }

    return 'not_started';
  }
}
